"use client";

import { LayoutList, Grid2X2, LucideMap } from "lucide-react";
import Image from "next/image";
import { useQueryState } from "nuqs";
import { useState, useEffect, useMemo, useCallback } from "react";

import { ListingStatus } from "@/.zenstack/logical-prisma-client";
import { ListingCard } from "@/components/listings/ListingCard";
import { ListingCardSkeleton } from "@/components/listings/ListingCardSkeleton";
import { tenantColumns } from "@/components/listings-table/columns/tenant";
import { ListingsTable } from "@/components/listings-table/ListingsTable";
import MapView from "@/components/MapView";
import { PaginationControls } from "@/components/ui/PaginationControls";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useDebounce } from "@/hooks/use-debounce";
import { LISTING_DETAILED_SELECT } from "@/hooks/use-listings";
import { useMounted } from "@/hooks/use-mounted";
import { useFindManyListing } from "@/lib/hooks/generated/tanstack-query";

import { SearchFilter } from "./SearchFilter";

const ITEMS_PER_PAGE = 12;

type SortOption = "price-asc" | "price-desc" | "ending-soonest";

export default function BrowsePage() {
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState<SortOption>("ending-soonest");
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const isMounted = useMounted();

  /**
   * Deep linking with search params for OAuth redirect flows
   *
   * @see ADR-018 - Type-Safe Deep Linking with OAuth Redirect Support
   *
   * Example deep link URLs:
   * - /browse?view=map
   *   → Opens map view after OAuth redirect
   *
   * - /browse?view=table&sort=price:asc
   *   → Shows table view with price sorting
   *
   * - /browse?view=card&filters={...}
   *   → Shows card view with filter params applied
   *
   * Note: View state uses search params (shareable, server-renderable)
   * instead of hash actions (client-only triggers)
   */
  const [viewParam, setViewParam] = useQueryState("view", {
    defaultValue: "cards",
  });

  // Sync view param with active tab state
  const activeTab = viewParam || "cards";
  const setActiveTab = (value: string) => {
    setViewParam(value);
  };

  // Reset to first page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchQuery]);

  // Calculate skip value for pagination
  const skip = (currentPage - 1) * ITEMS_PER_PAGE;

  // Build orderBy object based on sort option
  const orderBy = useMemo(() => {
    switch (sortBy) {
      case "price-asc":
        return { atAuction: { startingBid: "asc" as const } } as const;
      case "price-desc":
        return { atAuction: { startingBid: "desc" as const } } as const;
      case "ending-soonest":
      default:
        return { atAuction: { auctionEndDate: "desc" as const } } as const;
    }
  }, [sortBy]);

  // Build where clause with search functionality
  const whereClause = useMemo(() => {
    const baseWhere = {
      status: ListingStatus.ACTIVE,
    } as const;

    if (!debouncedSearchQuery.trim()) {
      return baseWhere;
    }

    return {
      ...baseWhere,
      OR: [
        {
          title: {
            contains: debouncedSearchQuery,
            mode: "insensitive" as const,
          },
        },
        {
          description: {
            path: ["$"],
            string_contains: debouncedSearchQuery,
          },
        },
        {
          details: {
            path: ["address", "streetLine1"],
            string_contains: debouncedSearchQuery,
          },
        },
        {
          details: {
            path: ["address", "city"],
            string_contains: debouncedSearchQuery,
          },
        },
        {
          details: {
            path: ["address", "state"],
            string_contains: debouncedSearchQuery,
          },
        },
        {
          business: {
            businessName: {
              contains: debouncedSearchQuery,
              mode: "insensitive" as const,
            },
          },
        },
      ],
    };
  }, [debouncedSearchQuery]);

  const { data: listings, isLoading } = useFindManyListing({
    include: LISTING_DETAILED_SELECT,
    where: whereClause,
    take: ITEMS_PER_PAGE,
    skip,
    orderBy,
  });

  // Get total count for pagination
  const { data: totalCount } = useFindManyListing({
    where: whereClause,
    select: {
      id: true,
    },
  });

  const handleSortChange = useCallback((value: string) => {
    setSortBy(value as SortOption);
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
  }, []);

  // Common empty state component
  const EmptyState = () => (
    <div className="flex flex-col items-center justify-center py-16">
      <Image
        src="/empty_listings.png"
        alt="No properties found"
        width={160}
        height={160}
        className="mb-6 h-40 w-40 rounded-md"
      />
      <div className="text-center">
        <h3 className="text-md mb-2 font-semibold">No properties were found</h3>
        <p className="text-sm text-secondary-foreground">
          Try adjusting your search filters or check back later for new
          listings.
        </p>
      </div>
    </div>
  );

  return (
    <div className="container mx-auto flex items-center justify-between px-6 py-8">
      <div className="container mb-10 flex w-full flex-col">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold">Browse listings</h1>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          {/* Property count */}
          <div className="flex items-center justify-end md:justify-between">
            <div className="hidden items-center gap-2 md:flex">
              {!isMounted || isLoading ? (
                <div className="h-4 w-48 animate-pulse rounded-md bg-muted" />
              ) : (
                <span className="text-[14px] text-muted-foreground">
                  {totalCount?.length || "No"} properties found
                  {debouncedSearchQuery && (
                    <span className="ml-1 text-muted-foreground">
                      for &quot;{debouncedSearchQuery}&quot;
                    </span>
                  )}
                </span>
              )}
            </div>

            {/* Filter, sort, and search */}
            <div className="flex items-center gap-2">
              {activeTab !== "map" && (
                <Select value={sortBy} onValueChange={handleSortChange}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ending-soonest">Ending soon</SelectItem>
                    <SelectItem value="price-asc">
                      Price: low to high
                    </SelectItem>
                    <SelectItem value="price-desc">
                      Price: high to low
                    </SelectItem>
                  </SelectContent>
                </Select>
              )}

              {/* Search */}
              <SearchFilter value={searchQuery} onChange={handleSearchChange} />

              {/* View Toggle */}
              <ToggleGroup
                type="single"
                value={activeTab}
                onValueChange={(value) => value && setActiveTab(value)}
                className="inline-flex h-10 shrink-0 items-center overflow-hidden border border-input bg-transparent"
              >
                {/* Cards View */}
                <ToggleGroupItem
                  value="cards"
                  className="h-full w-[38px] flex-1 cursor-pointer rounded-none border-r border-input px-2 text-sm font-medium text-muted-foreground data-[state=on]:text-foreground"
                >
                  <Grid2X2 className="h-4 w-4" />
                </ToggleGroupItem>

                {/* Table View */}
                <ToggleGroupItem
                  value="table"
                  className="w-[38px]flex-1 h-full cursor-pointer rounded-none border-r border-input px-2 text-sm font-medium text-muted-foreground data-[state=on]:text-foreground"
                >
                  <LayoutList className="h-4 w-4" />
                </ToggleGroupItem>

                {/* Map View */}
                <ToggleGroupItem
                  value="map"
                  className="h-full w-[38px] flex-1 cursor-pointer rounded-none px-2 text-sm font-medium text-muted-foreground data-[state=on]:text-foreground"
                >
                  <LucideMap className="h-4 w-4" />
                </ToggleGroupItem>
              </ToggleGroup>
            </div>
          </div>

          {/* Cards View */}
          <TabsContent value="cards" className="m-0">
            {!isMounted || isLoading ? (
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                {Array.from({ length: 6 }).map((_, index) => (
                  <ListingCardSkeleton key={index} />
                ))}
              </div>
            ) : listings && listings.length > 0 ? (
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                {listings.map((listing) => (
                  <ListingCard key={listing.id} listing={listing} />
                ))}
              </div>
            ) : (
              <EmptyState />
            )}
          </TabsContent>

          {/* Table View */}
          <TabsContent value="table" className="m-0">
            {!isMounted || isLoading ? (
              <div className="h-96 w-full animate-pulse bg-muted" />
            ) : listings && listings.length > 0 ? (
              <ListingsTable
                data={listings}
                columns={tenantColumns}
                isLoading={false}
              />
            ) : (
              <EmptyState />
            )}
          </TabsContent>

          {/* Map View */}
          <TabsContent value="map" className="m-0">
            <div className="overflow-hidden rounded-lg border">
              {!isMounted || isLoading ? (
                <div className="h-[600px] w-full animate-pulse bg-muted" />
              ) : listings && listings.length > 0 ? (
                <div className="h-[600px] w-full">
                  <MapView
                    listings={listings}
                    googleMapId={
                      process.env.NEXT_PUBLIC_GOOGLE_MAPS_BROWSE_MAP_ID!
                    }
                  />
                </div>
              ) : (
                <div className="flex h-[600px] w-full items-center justify-center">
                  <EmptyState />
                </div>
              )}
            </div>
          </TabsContent>
          <div className="mt-8 flex justify-center">
            <PaginationControls
              currentPage={currentPage}
              onPageChange={handlePageChange}
              pageSize={ITEMS_PER_PAGE}
              totalCount={totalCount?.length ?? 0}
              showIfSinglePage={false}
            />
          </div>
        </Tabs>
      </div>
    </div>
  );
}
